import React, { useState } from 'react';
import { Box, Button, Grid, Link, Typography } from '@mui/material';
import CustomTextField from '../../../../components/CustomTextField/CustomTextField';
import { FormProvider, useForm } from 'react-hook-form';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SelectableBoxCommon from './SelectableBoxCommon';
import ActionButtonCommon from './ActionButtonCommon';
import NameAndPronounsFields from './NameAndPronounsFields';
import EmailPasswordFields from './EmailPasswordFields';
import BiographyField from './BiographyField';
import DegreesField from './DegreesField';
import CertificationsField from './CertificationsField';
import InsuranceUploaded from './InsuranceUploaded';
import ManageSocialAccountsFields from './ManageSocialAccountsFields';
import { useNavigate } from 'react-router-dom';
import LicenseUploaded from './LicenseUploaded';
import { useAppData } from 'context/AppContext';
import swal from 'sweetalert';
import { IUser } from 'types';

type SelectedValueType =
  | 'nameAndPronouns'
  | 'emailPassword'
  | 'biography'
  | 'degrees'
  | 'certifications'
  | 'license'
  | 'insurance'
  | 'manageSocialAccounts';

type PersonalInformationProps = {
  user: IUser | null;
};

const PersonalInformation = (props: PersonalInformationProps) => {
  const { user } = props;
  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>('nameAndPronouns');
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const { updateUserById } = useAppData();

  // Initialize state for license data
  const [licenseData, setLicenseData] = useState({
    licenseImageUrl:
      user?.licenseImageUrl ||
      'https://firebasestorage.googleapis.com/v0/b/affekti---project.appspot.com/o/files%2Ffd4cc0bd-4b5a-41f4-8f7d-81c3fe4ab63b?alt=media&token=8790c05d-39ac-4c44-8101-3bac014f8ec3',
    licenseExpiration: user?.licenseExpiration || '',
    licenseNumber: user?.licenseNumber || '',
  });

  // Initialize state for license data
  const [insuranceData, setInsuranceData] = useState({
    insuranceImageUrl:
      user?.insuranceImageUrl ||
      'https://firebasestorage.googleapis.com/v0/b/affekti---project.appspot.com/o/files%2Ffd4cc0bd-4b5a-41f4-8f7d-81c3fe4ab63b?alt=media&token=8790c05d-39ac-4c44-8101-3bac014f8ec3',
    insuranceExpiration: user?.insuranceExpiration || '',
    insuranceNumber: user?.insuranceNumber || '',
  });


  const handleLicenseDataChange = (data: any) => {
    setLicenseData(data);
  };
  const handleInsuranceDataChange = (data: any) => {
    setInsuranceData(data);
  };

  const [showIcons, setShowIcons] = useState({});

  const steps: SelectedValueType[] = [
    'nameAndPronouns',
    'emailPassword',
    'biography',
    'degrees',
    'certifications',
    'license',
    'insurance',
    'manageSocialAccounts',
  ];

  const headers: Record<SelectedValueType, string> = {
    nameAndPronouns: 'Name and Pronouns',
    emailPassword: 'Email Address & Password',
    biography: 'Biography',
    degrees: 'Degrees',
    certifications: 'Certifications',
    license: 'License',
    insurance: 'Insurance',
    manageSocialAccounts: 'Manage Social Accounts',
  };

  const handleSelectionChange = (value: SelectedValueType) => {
    setSelectedValue(value);
  };

  const handleNextClick = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedValue(steps[currentStep + 1]);
    }
  };

  const handleNavigateContents = () => {
    navigate('/contents');
  };
  const handleNavigateLearning = () => {
    navigate('/my-learning');
  };
  const handleNavigatePayments = () => {
    navigate('/payments');
  };
  const handleSaveChanges = () => {
    if (user) {
      updateUserById(user.id, user)
        .then((response: any) => {
          if (response.success) {
            swal('Success', 'User updated successfully!', 'success');
          } else {
            swal(
              'Error',
              `Failed to update user: ${response.message}`,
              'error'
            );
          }
        })
        .catch((error: any) => {
          swal('Error', `Error updating user: ${error.message}`, 'error');
        });
    }
  };
  // const handleNavigateSettings = () => {
  //     navigate('/settings');
  // };

  return (
    <>
      <Box display='flex' minHeight='100vh'>
        <Box width='22%' p={2}>
          <Typography
            variant='h6'
            gutterBottom
            fontSize={18}
            fontWeight={700}
            color={'#595959'}>
            User Account
          </Typography>

          <SelectableBoxCommon
            selectedValue={selectedValue}
            value='nameAndPronouns'
            onClick={handleSelectionChange}
            text='Name and Pronouns'
          />

          <Typography
            variant='h6'
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={'#595959'}>
            Security Settings
          </Typography>

          <SelectableBoxCommon
            selectedValue={selectedValue}
            value='emailPassword'
            onClick={handleSelectionChange}
            text='Email Address & Password'
          />

          <Typography
            variant='h6'
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={'#595959'}>
            Content Creator Account
          </Typography>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='biography'
              onClick={handleSelectionChange}
              text='Biography'
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='degrees'
              onClick={handleSelectionChange}
              text='Degrees'
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='certifications'
              onClick={handleSelectionChange}
              text='Certifications'
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='license'
              onClick={handleSelectionChange}
              text='License'
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='insurance'
              onClick={handleSelectionChange}
              text='Insurance'
            />
          </Box>
          <Typography
            variant='h6'
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={'#595959'}>
            Social Accounts
          </Typography>

          <SelectableBoxCommon
            selectedValue={selectedValue}
            value='manageSocialAccounts'
            onClick={handleSelectionChange}
            text='Manage Social Accounts'
          />
          <Box
            display={'flex'}
            justifyContent={'flex-end'}
            mt={2}
            flexDirection={'column'}>
            <Button
              onClick={handleSaveChanges}
              sx={{
                width: '214px',
                height: '48px',
                p: '6px 20px',
                backgroundColor: '#233F91',
                borderRadius: '15px',
                color: 'white',
              }}
              type='submit'>
              Save Changes
            </Button>

            {/* <Box display={'flex'} flexDirection={'column'} gap={1} mt={3}>
              <ActionButtonCommon
                text='View Learning'
                onClick={handleNavigateLearning}
                width='158px'
                height='42px'
              />
              <ActionButtonCommon
                text='User’s Contents'
                onClick={handleNavigateContents}
                width='163px'
                height='42px'
              />
              <ActionButtonCommon
                text='User’s Payouts'
                onClick={handleNavigatePayments}
                width='155px'
                height='42px'
              />
              <ActionButtonCommon
                text='User’s Payments'
                // onClick={handleViewPaymentsClick}
                width='165px'
                height='42px'
              />
            </Box> */}
          </Box>
        </Box>

        <Box
          width='80%'
          height='100vh'
          position={'relative'}
          bgcolor={'#F8F8F8'}
          sx={{
            borderRadius: '10px',
            border: '1px solid #A9A9A9',
            overflow: 'hidden',
            overflowY: 'scroll',
          }}>
          <Typography
            variant='h5'
            gutterBottom
            fontSize={18}
            p={3}
            fontWeight={700}
            color={'#797979'}>
            {headers[selectedValue]}
          </Typography>
          <Typography
            sx={{
              borderBottom: '1px solid #A9A9A9',
              width: '100%',
            }}></Typography>

          {selectedValue === 'nameAndPronouns' ? (
            <NameAndPronounsFields
              firstName={user?.firstName}
              lastName={user?.lastName}
              pronouns={user?.pronouns}
            />
          ) : selectedValue === 'emailPassword' ? (
            <EmailPasswordFields email={user?.email} />
          ) : selectedValue === 'biography' ? (
            <BiographyField biography={user?.biography} />
          ) : selectedValue === 'degrees' ? (
            <DegreesField degrees={user?.degrees} />
          ) : selectedValue === 'certifications' ? (
            <CertificationsField certifications={user?.certifications} />
          ) : selectedValue === 'license' ? (
            <LicenseUploaded
              licenseData={licenseData}
              setLicenseData={handleLicenseDataChange}
              onChange={handleLicenseDataChange}
              setShowIcons={setShowIcons}
            />
          ) : selectedValue === 'insurance' ? (
            <InsuranceUploaded
              insuranceData={insuranceData}
              setLicenseData={handleInsuranceDataChange}
              onChange={handleInsuranceDataChange}
              setShowIcons={setShowIcons}
            />
          ) : selectedValue === 'manageSocialAccounts' ? (
            <ManageSocialAccountsFields
              instagram={user?.instagram}
              facebook={user?.facebook}
              linkedin={user?.linkedin}
              other={user?.other}
              twitter={user?.twitter}
              personalWebsite={user?.personalWebsite}
            />
          ) : null}

          <Box
            position='absolute'
            bottom={0}
            width='100%'
            p={2}
            bgcolor='#F8F8F8'
            display='flex'
            justifyContent='center'>
            <Button
              sx={{
                color: '#595959',
                bgcolor: '#DBDBDB',
                borderRadius: '10px',
                border: '1px solid #B3B3B3',
                fontWeight: 400,
                fontSize: '15px',
              }}
              onClick={handleNextClick}>
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PersonalInformation;
